import { useEffect, useRef, useState } from 'react'

import { ActionIcon, Loader, MultiSelect, Select, TextInput, Tooltip } from '@mantine/core'
import { DateInput } from '@mantine/dates'
import { Clock } from 'lucide-react'

import Row from './Row/Row'
import {
  formatValueForDisplay,
  getValueForFieldType,
} from './customFieldsUtils'

const CustomFieldRow = ({
  field,
  value,
  readOnly = false,
  onShowHistory,
  onSave,
  saving = false,
}) => {
  const [isEditing, setIsEditing] = useState(false)
  const [localValue, setLocalValue] = useState(null)
  const [isHovered, setIsHovered] = useState(false)
  const containerRef = useRef(null)
  const saveTimeoutRef = useRef(null)
  const localValueRef = useRef(localValue)
  const hasEditedRef = useRef(false)
  const latestStateRef = useRef({
    saving,
    value,
    field,
    onSave,
  })
  const normalizeDateValue = (valueToNormalize) => {
    if (valueToNormalize === null || valueToNormalize === undefined) {
      return valueToNormalize
    }

    if (typeof valueToNormalize === 'string') {
      const trimmedValue = valueToNormalize.trim()
      if (/^\d{4}-\d{2}-\d{2}$/.test(trimmedValue)) {
        return trimmedValue
      }
    }

    const parsedDate =
      valueToNormalize instanceof Date
        ? valueToNormalize
        : new Date(valueToNormalize)

    if (Number.isNaN(parsedDate.getTime())) {
      return valueToNormalize
    }

    const year = parsedDate.getFullYear()
    const month = String(parsedDate.getMonth() + 1).padStart(2, '0')
    const day = String(parsedDate.getDate()).padStart(2, '0')

    return `${year}-${month}-${day}`
  }

  const getComparableValue = (fieldType, valueToCompare) => {
    if (fieldType === 'DATE') {
      return normalizeDateValue(valueToCompare)
    }

    return valueToCompare
  }

  const hasValue = value != null

  // Save unsaved changes on unmount (e.g., sidebar close, applicant switch)
  useEffect(() => {
    return () => {
      if (saveTimeoutRef.current) {
        clearTimeout(saveTimeoutRef.current)
      }
      if (hasEditedRef.current) {
        const { saving, value, field, onSave } = latestStateRef.current
        if (!saving) {
          const currentValue = getValueForFieldType(field.fieldType, value)
          const comparableLocal = getComparableValue(field.fieldType, localValueRef.current)
          const comparableCurrent = getComparableValue(field.fieldType, currentValue)
          const hasChanged =
            JSON.stringify(comparableLocal) !== JSON.stringify(comparableCurrent)
          const isEmpty =
            localValueRef.current === null ||
            localValueRef.current === undefined ||
            localValueRef.current === '' ||
            (Array.isArray(localValueRef.current) && localValueRef.current.length === 0)
          if (hasChanged && !(isEmpty && !value)) {
            onSave(field, localValueRef.current)
          }
        }
      }
    }
  }, [])

  useEffect(() => {
    latestStateRef.current = {
      saving,
      value,
      field,
      onSave,
    }
  }, [saving, value, field, onSave])

  const setLocalValueAndRef = (nextValue) => {
    localValueRef.current = nextValue
    setLocalValue(nextValue)
  }

  const handleStartEdit = () => {
    if (readOnly || saving) return
    setLocalValueAndRef(getValueForFieldType(field.fieldType, value))
    hasEditedRef.current = false
    setIsEditing(true)
  }

  const handleSelectChange = async (newValue) => {
    setLocalValueAndRef(newValue)
    hasEditedRef.current = true
    if (field.fieldType === 'SINGLE_SELECT') {
      // Auto-save single select immediately after selection
      if (saveTimeoutRef.current) {
        clearTimeout(saveTimeoutRef.current)
      }
      saveTimeoutRef.current = setTimeout(async () => {
        await onSave(field, newValue)
        hasEditedRef.current = false
        setIsEditing(false)
        saveTimeoutRef.current = null
      }, 100)
    }
    // MULTI_SELECT: just update local value — save happens on click-outside or unmount
  }

  const handleBooleanChange = async (checked) => {
    setLocalValueAndRef(checked)
    hasEditedRef.current = true
    // Clear any existing timeout
    if (saveTimeoutRef.current) {
      clearTimeout(saveTimeoutRef.current)
    }
    // Auto-save boolean immediately after change
    saveTimeoutRef.current = setTimeout(async () => {
      await onSave(field, checked)
      hasEditedRef.current = false
      setIsEditing(false)
      saveTimeoutRef.current = null
    }, 100)
  }

  const handleDateChange = async (date) => {
    setLocalValueAndRef(date)
    hasEditedRef.current = true
    if (saveTimeoutRef.current) {
      clearTimeout(saveTimeoutRef.current)
    }
    // Auto-save after date selection
    saveTimeoutRef.current = setTimeout(async () => {
      await onSave(field, date)
      hasEditedRef.current = false
      setIsEditing(false)
      saveTimeoutRef.current = null
    }, 100)
  }

  const handleKeyDown = async (e) => {
    if (e.key === 'Escape') {
      // Cancel any pending auto-save
      if (saveTimeoutRef.current) {
        clearTimeout(saveTimeoutRef.current)
        saveTimeoutRef.current = null
      }
      hasEditedRef.current = false
      setIsEditing(false)
      setLocalValueAndRef(null)
    } else if (e.key === 'Enter' && field.fieldType === 'STRING') {
      e.preventDefault()
      // Cancel any pending auto-save before manual save
      if (saveTimeoutRef.current) {
        clearTimeout(saveTimeoutRef.current)
        saveTimeoutRef.current = null
      }
      await onSave(field, localValueRef.current)
      hasEditedRef.current = false
      setIsEditing(false)
    }
  }

  // Handle click outside to save
  useEffect(() => {
    if (!isEditing) return

    const handleClickOutside = async (event) => {
      const { saving, value, field, onSave } = latestStateRef.current
      const localValue = localValueRef.current

      // Only check for portal clicks for field types that use portals (SELECT, MULTI_SELECT, DATE, BOOLEAN)
      const usesPortal = ['SINGLE_SELECT', 'MULTI_SELECT', 'DATE', 'BOOLEAN'].includes(field.fieldType)

      if (usesPortal) {
        // Check if click is on a Mantine portal (dropdown, date picker, etc.)
        // event.target might be Document node, so check if it's an Element first
        const target = event.target
        const isElement = target instanceof Element

        const isClickOnPortal = isElement && (
          target.closest('[data-portal]') ||
          target.closest('.mantine-Select-dropdown') ||
          target.closest('.mantine-MultiSelect-dropdown') ||
          target.closest('.mantine-DateInput-dropdown') ||
          target.closest('.mantine-Popover-dropdown')
        )

        if (isClickOnPortal) {
          return // Don't save if clicking on dropdown
        }
      }

      if (containerRef.current && !containerRef.current.contains(event.target)) {
        if (!saving) {
          // Cancel any pending auto-save to prevent duplicate saves
          if (saveTimeoutRef.current) {
            clearTimeout(saveTimeoutRef.current)
            saveTimeoutRef.current = null
          }

          // Only save if value has changed
          const currentValue = getValueForFieldType(field.fieldType, value)
          const comparableLocalValue = getComparableValue(field.fieldType, localValue)
          const comparableCurrentValue = getComparableValue(
            field.fieldType,
            currentValue
          )
          const hasChanged =
            JSON.stringify(comparableLocalValue) !==
            JSON.stringify(comparableCurrentValue)

          // Check if the new value is empty/cleared
          const isEmpty =
            localValue === null ||
            localValue === undefined ||
            localValue === '' ||
            (Array.isArray(localValue) && localValue.length === 0)

          // Only save if changed and not just opening an empty field
          if (hasChanged && !(isEmpty && !value)) {
            await onSave(field, localValue)
          }
          hasEditedRef.current = false
          setIsEditing(false)
        }
      }
    }

    // Small delay to prevent the click that started editing from triggering save
    const timeoutId = setTimeout(() => {
      document.addEventListener('mousedown', handleClickOutside)
    }, 100)

    return () => {
      clearTimeout(timeoutId)
      document.removeEventListener('mousedown', handleClickOutside)
    }
  }, [isEditing])

  return (
    <Row
      tooltipText={field.label + (field.isRequired ? ' *' : '')}
      value=""
    >
      <div
        ref={containerRef}
        className="flex w-full min-w-0 items-center gap-1"
        onMouseEnter={() => setIsHovered(true)}
        onMouseLeave={() => setIsHovered(false)}
      >
        <div
          className={`min-w-0 flex-1 overflow-hidden ${
            !readOnly && !isEditing && !saving
              ? 'cursor-pointer rounded px-1 hover:bg-gray-50'
              : ''
          }`}
          onClick={!isEditing ? handleStartEdit : undefined}
        >
          {saving ? (
            <div className="flex items-center gap-2">
              <Loader size="xs" />
              <span className="text-xs text-doubleNickel-gray-500">Saving...</span>
            </div>
          ) : (
            renderInput(
              field,
              isEditing ? localValue : value,
              isEditing,
              setLocalValueAndRef,
              readOnly,
              value,
              isHovered && !readOnly,
              handleKeyDown,
              handleSelectChange,
              handleBooleanChange,
              handleDateChange
            )
          )}
        </div>

        {/* Action buttons when not editing - always visible */}
        {!isEditing && !saving && hasValue && onShowHistory && (
          <Tooltip label="View change history">
            <ActionIcon
              variant="subtle"
              color="gray"
              size="sm"
              className="flex-shrink-0"
              onClick={() => onShowHistory(value, field)}
            >
              <Clock className="h-3.5 w-3.5" />
            </ActionIcon>
          </Tooltip>
        )}
      </div>
    </Row>
  )
}

const renderInput = (
  field,
  displayValue,
  editing,
  onChange,
  readOnly,
  value,
  isHovered,
  onKeyDown,
  onSelectChange,
  onBooleanChange,
  onDateChange
) => {
  if (readOnly || !editing) {
    const displayText = formatValueForDisplay(field, value)
    const shouldShowTooltip = displayText && displayText.length > 20

    return (
      <div className="flex min-w-0 items-center gap-1 overflow-hidden">
        {shouldShowTooltip ? (
          <Tooltip label={displayText} withArrow multiline maw={250}>
            <span className="block cursor-help truncate text-xs text-doubleNickel-gray-900">
              {displayText}
            </span>
          </Tooltip>
        ) : (
          <span className="block truncate text-xs text-doubleNickel-gray-900">
            {displayText || <span className="text-doubleNickel-gray-400">—</span>}
          </span>
        )}
        {!readOnly && isHovered && (
          <span className="flex-shrink-0 whitespace-nowrap text-xs text-doubleNickel-gray-400">(edit)</span>
        )}
      </div>
    )
  }

  // Prevent click propagation when interacting with inputs
  const handleClick = (e) => e.stopPropagation()

  switch (field.fieldType) {
    case 'STRING':
      return (
        <TextInput
          value={displayValue || ''}
          onChange={(e) => onChange(e.target.value)}
          onClick={handleClick}
          onKeyDown={onKeyDown}
          size="xs"
          className="w-full max-w-[200px]"
          placeholder={field.label}
          autoFocus
        />
      )

    case 'BOOLEAN':
      return (
        <Select
          value={
            displayValue === true
              ? 'true'
              : displayValue === false
                ? 'false'
                : null
          }
          onChange={(val) =>
            onBooleanChange(val === null ? null : val === 'true')
          }
          onClick={handleClick}
          onKeyDown={onKeyDown}
          data={[
            { value: 'true', label: 'Yes' },
            { value: 'false', label: 'No' },
          ]}
          size="xs"
          className="w-full max-w-[200px]"
          placeholder="Select"
          clearable
          allowDeselect={false}
        />
      )

    case 'DATE':
      return (
        <DateInput
          value={displayValue ? new Date(displayValue) : null}
          onChange={onDateChange}
          onClick={handleClick}
          onKeyDown={onKeyDown}
          size="xs"
          className="w-full max-w-[200px]"
          placeholder="Select date"
          clearable
        />
      )

    case 'SINGLE_SELECT':
      return (
        <Select
          value={displayValue?.[0] ?? null}
          onChange={(val) => {
            const newValue = val != null ? [val] : []
            onSelectChange(newValue)
          }}
          onClick={handleClick}
          onKeyDown={onKeyDown}
          data={field.options?.map((opt) => ({
            label: opt.label,
            value: opt.value,
          }))}
          size="xs"
          className="w-full max-w-[200px]"
          placeholder="Select option"
          clearable
        />
      )

    case 'MULTI_SELECT':
      return (
        <MultiSelect
          value={displayValue || []}
          onChange={(val) => {
            onSelectChange(val)
          }}
          onClick={handleClick}
          onKeyDown={onKeyDown}
          data={field.options?.map((opt) => ({
            label: opt.label,
            value: opt.value,
          }))}
          size="xs"
          className="w-full max-w-[200px]"
          placeholder="Select options"
          clearable
        />
      )

    default:
      return <span className="text-xs text-doubleNickel-gray-500">—</span>
  }
}

export default CustomFieldRow
